import React, { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { CircularProgress, Dialog, IconButton, Popover, useMediaQuery } from '@material-ui/core';
import { ZoomIn, BrokenImageSharp } from '@material-ui/icons';
import clsx from 'clsx';
import PropTypes from 'prop-types';

const useStyles = makeStyles(
  (theme) => ({
    root: {
      position: 'relative',
      display: 'flex',
      '&:hover $imageWrapper': {
        opacity: 1,
      },
    },
    popover: {
      pointerEvents: 'none',
    },
    image: {
      maxWidth: '100%',
      opacity: 1,
      transition: 'opacity ease-in-out 0.1s',
    },
    imageLoading: {
      opacity: 0,
    },
    imageWrapper: {
      maxWidth: 400,
      maxHeight: 400,
      minWidth: 200,
      minHeight: 200,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: theme.spacing(0.5),
    },
    zoomIn: {
      position: 'absolute',
      right: theme.spacing(1),
      bottom: theme.spacing(1),
      color: 'white',
      '& svg': {
        filter: 'drop-shadow(0px 0px 1px rgb(0 0 0 / 0.7))',
      },
    },
    paperRoot: {
      background: 'transparent',
      borderRadius: 0,
    },
    progress: {
      margin: 'auto',
      position: 'absolute',
      left: '50%',
      top: '50%',
      marginLeft: -20,
      marginTop: -20,
    },
    brokenImageIcon: {
      fontSize: 210,
    },
    thumbnailImage: {
      opacity: 0,
      transition: 'all ease-in-out 0.3s',
    },
    thumbnailImageLoaded: {
      opacity: 1,
    },
  }),
  { name: 'Magnifier' }
);

const LoadingStates = {
  loading: 'loading',
  finished: 'finished',
  failed: 'failed',
};

const ThumbnailImage = ({ className, ...props }) => {
  const [loadingState, setLoadingState] = useState('');
  useEffect(() => {
    setLoadingState('');
    if (!props.src) {
      return;
    }
    const timeout = setTimeout(
      () => setLoadingState((loadingState) => (loadingState === '' ? LoadingStates.loading : loadingState)),
      500
    );
    return () => clearTimeout(timeout);
  }, [props.src]);
  const classes = useStyles();
  if (!props.src) {
    return null;
  }
  return (
    <>
      <img
        {...props}
        onLoad={() => setLoadingState(LoadingStates.finished)}
        onError={() => setLoadingState(LoadingStates.failed)}
        className={clsx(
          className,
          classes.thumbnailImage,
          loadingState === 'finished' && classes.thumbnailImageLoaded
        )}
      />
      {loadingState === LoadingStates.loading && (
        <CircularProgress color="primary" className={classes.progress} />
      )}
    </>
  );
};

ThumbnailImage.propTypes = {
  className: PropTypes.string,
  src: PropTypes.string.isRequired,
};

export const Magnifier = ({ src, alt, thumbnailSrc, thumbnailClassName }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [backdropOpen, setBackdropOpen] = useState(false);
  const [loadingState, setLoadingState] = useState('');
  const classes = useStyles();

  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('xs'));

  const handlePopoverOpen = (evt) => {
    setLoadingState(src ? LoadingStates.loading : LoadingStates.failed);
    setAnchorEl(evt.currentTarget);
  };

  const handlePopoverClose = () => {
    setLoadingState('');
    setAnchorEl(null);
  };

  const handleBackdropOpen = (evt) => {
    evt.stopPropagation();
    setLoadingState(src ? LoadingStates.loading : LoadingStates.failed);
    setBackdropOpen(true);
  };

  const handleBackdropClose = (evt) => {
    evt.stopPropagation();
    setLoadingState('');
    setBackdropOpen(false);
  };

  const createLoadingStateHandler = (state) => () => setLoadingState(state);

  const popoverOpen = Boolean(anchorEl);

  const loadingInProgress = loadingState === LoadingStates.loading;

  const renderImage = () => (
    <div className={classes.imageWrapper}>
      {loadingInProgress && <CircularProgress color="primary" className={classes.progress} />}
      {loadingState !== LoadingStates.failed ? (
        <img
          className={clsx(classes.image, loadingInProgress && classes.imageLoading)}
          src={src}
          alt={alt}
          onLoad={createLoadingStateHandler(LoadingStates.finished)}
          onError={createLoadingStateHandler(LoadingStates.failed)}
        />
      ) : (
        <BrokenImageSharp className={classes.brokenImageIcon} />
      )}
    </div>
  );

  if (!thumbnailSrc) {
    return (
      <div className={classes.root}>
        <div className={thumbnailClassName} />
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <ThumbnailImage
        src={thumbnailSrc}
        alt={alt}
        aria-owns={popoverOpen ? 'mouse-over-popover' : undefined}
        aria-haspopup="true"
        onMouseEnter={handlePopoverOpen}
        onMouseLeave={handlePopoverClose}
        className={thumbnailClassName}
      />

      {isMobile ? (
        <>
          <IconButton aria-label="zoom in" className={classes.zoomIn} onClick={handleBackdropOpen}>
            <ZoomIn />
          </IconButton>

          <Dialog
            onClick={handleBackdropClose}
            open={backdropOpen}
            onClose={handleBackdropClose}
            PaperProps={{
              elevation: 0,
              classes: {
                root: classes.paperRoot,
              },
            }}
          >
            {renderImage()}
          </Dialog>
        </>
      ) : (
        <Popover
          id="mouse-over-popover"
          className={classes.popover}
          open={popoverOpen}
          anchorEl={anchorEl}
          anchorOrigin={{
            vertical: 'center',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          onClose={handlePopoverClose}
          disableRestoreFocus
        >
          {renderImage()}
        </Popover>
      )}
    </div>
  );
};

Magnifier.propTypes = {
  src: PropTypes.string.isRequired,
  alt: PropTypes.string.isRequired,
  thumbnailSrc: PropTypes.string.isRequired,
  thumbnailClassName: PropTypes.string,
};

export default Magnifier;
